import { z } from "zod";
import { Uuid } from "./common.ts";

/*
 * The product texts the portal reads from the DC-API-Gateway under `/dc` (07 §5, ADR-048,
 * ADR-057, Informationspaket 1.3 and 1.5). Not a route of the platform API: the gateway passes
 * an endpoint of the central dataCycle instance through, and the portal joins the texts to the
 * catalogue by `Product.datacycleId` (ADR-054). The platform owns identity, prices and
 * decisions; dataCycle owns the words. Texts are HTML written by editors, so whoever shows them
 * sanitises them first.
 *
 * Browser-safe like the rest of the package.
 */

/**
 * The fields the portal asks for; dataCycle sends only these (and `@id`, `@type`, `dc:touched`).
 * `image.dc:touched` and `logo.dc:touched` narrow the key visual and the product's logo to their
 * id, change stamp and the renditions the gateway adds: the portal loads both over the gateway's
 * image route, not from the instance (the logo, an SVG in dataCycle, as a raster with its own
 * colours on the band of the catalogue's cards). `dc:tool.name` names the pixies of a dataPixie
 * bundle on its card (checked against the gateway on 2026-10-29). The nested concept fields
 * narrow each concept to label, slug, description and tree (checked against the gateway on
 * 2026-10-14; `dc:classification` alone drops slug and description).
 */
export const DC_PRODUCT_FIELDS = [
  "name",
  "dc:slug",
  "disambiguatingDescription",
  "description",
  "image.dc:touched",
  "logo.dc:touched",
  "brand",
  "dc:tool.name",
  "dc:classification.skos:prefLabel",
  "dc:classification.dc:slugifiedName",
  "dc:classification.dct:description",
  "dc:classification.skos:inScheme",
] as const;

/**
 * The linked things the endpoint expands: the key visual and the logo with their renditions, the brand (the
 * maker, Informationspaket 1.3; its slug is the fallback for the product's line), the tools and the
 * concepts of the product with their tree (Informationspaket 1.4). Comma-separated like
 * `fields`; the gateway's item-level cache splits the string when it re-fetches changed items.
 */
export const DC_PRODUCT_INCLUDE = [
  "image",
  "logo",
  "brand",
  "dc:tool",
  "dc:classification",
  "dc:classification.skos:inScheme",
] as const;

/**
 * Page size of the request. The catalogue is a handful of products (`ProductListResponse` is not
 * paged either); one page of this size holds every published product of the endpoint.
 */
export const DC_PRODUCT_PAGE_SIZE = 100;

/*
 * The trees of the central instance the catalogue reads, by UUID (Informationspaket 1.4:
 * filters and facets work with UUIDs, labels are content). The same for every environment while
 * there is one central instance; an app may let dev override them, as the portal does with the
 * endpoint (ADR-090 point 4). The website's `dc_config.product_category_scheme` carries the first.
 */

/** "Produktkategorien": the categories for chips and grouping (Informationspaket 1.3). */
export const DC_PRODUCT_CATEGORY_SCHEME = "a387b125-22af-444b-9431-3af67a2e3a3c";

/** "Produkttypen" (since 2026-10-14): the line of a product, e.g. "dataPixie". */
export const DC_PRODUCT_TYPE_SCHEME = "11d36cf9-713b-4a2b-bb45-c1d392d0f1f0";

/*
 * The way of sale dataCycle states, two concepts of "Produktkategorien" (ADR-112 point 1,
 * ADR-147 point 3). The platform's own sale is a term of the product; the sync keeps dataCycle's
 * word beside it, and a new product starts with it.
 */

/** The concept "Anfrage": sold on request only, no order and no demo. */
export const DC_INQUIRY_CONCEPT = "6c079d1f-ae73-4c82-bea7-a4a6073bd0ba";

/** The concept "Preis auf Anfrage": orderable, without a display price. */
export const DC_PRICE_ON_REQUEST_CONCEPT = "e9ae836f-fd7f-4fe6-9dbd-77f05482d354";

/*
 * The concepts of "Produkttypen" the portal's catalogue knows, the ids of those product families
 * (ADR-112): it finds their shelf's place, the AI Cockpit stage and the bands by these ids, never
 * by the label, which marketing may change.
 */

/** The concept "AI Cockpit": its family stands first, on the dark stage (B-62). */
export const DC_AI_COCKPIT_CONCEPT = "0d377543-186e-47e7-bdb7-31d66ad07327";

/** The concept "feratel AI plus": its family stands second, on the feratel band (B-63). */
export const DC_FERATEL_CONCEPT = "86b03405-7661-42bc-b3fe-7e49bba08858";

/** The concept "dataPixie": its family stands third, on the dataPixies' yellow band. */
export const DC_DATAPIXIE_CONCEPT = "0fd4e011-f0fe-4d2d-960c-8dafe79b9320";

/** The concept "Bundle" (since 2026-10-28): the dataPixies' packages, one per area. */
export const DC_BUNDLE_CONCEPT = "8318406e-311f-48a7-b2f5-449c502bd65e";

/** Body of `POST /dc/endpoints/<endpoint-uuid>` for the products (Informationspaket 1.5). */
export const DcProductRequest = z.strictObject({
  /** comma-separated, `DC_PRODUCT_FIELDS` */
  fields: z.string().min(1),
  /** comma-separated, `DC_PRODUCT_INCLUDE` */
  include: z.string().min(1),
  /** the platform speaks German (Informationspaket 1.5) */
  language: z.literal("de"),
  page: z.strictObject({ size: z.number().int().min(1).max(DC_PRODUCT_PAGE_SIZE) }),
});
export type DcProductRequest = z.infer<typeof DcProductRequest>;

/** The request the portal sends: every field it shows, in German, one page. */
export const dcProductRequest: DcProductRequest = {
  fields: DC_PRODUCT_FIELDS.join(","),
  include: DC_PRODUCT_INCLUDE.join(","),
  language: "de",
  page: { size: DC_PRODUCT_PAGE_SIZE },
};

/** One rendition of an image as the gateway's imgproxy delivers it (`srcset.variants.<preset>`). */
export const DcImageVariant = z.object({ url: z.string(), width: z.number() });
export type DcImageVariant = z.infer<typeof DcImageVariant>;

/**
 * An image of a product. The portal shows it over the gateway's route
 * `GET /dc/assets/<preset>/<@id>?touched=<dc:touched>`, which streams the rendition on the
 * portal's own host (CSP `img-src 'self'`); the presets and widths are those of `srcset`, which
 * the gateway adds to every image. With `image.dc:touched` in `fields` nothing else arrives.
 */
export const DcImage = z.object({
  "@id": z.string(),
  "dc:touched": z.string().optional(),
  srcset: z.object({ variants: z.record(z.string(), DcImageVariant).optional() }).optional(),
});
export type DcImage = z.infer<typeof DcImage>;

/** The brand of a product: the maker as an Organization (Informationspaket 1.3), with its slug. */
export const DcBrand = z.object({
  "@id": z.string(),
  name: z.string().optional(),
  "dc:slug": z.string().optional(),
});
export type DcBrand = z.infer<typeof DcBrand>;

/**
 * A concept the product is classified with, and its tree (`skos:inScheme`). dataCycle sends
 * every tree in one list; `conceptsOf` picks a tree by its UUID (Informationspaket 1.4). The
 * slug is dataCycle's, for addresses; the description is HTML from the editors, like the texts.
 */
export const DcConcept = z.object({
  "@id": z.string(),
  "skos:prefLabel": z.string().optional(),
  "dc:slugifiedName": z.string().optional(),
  "dct:description": z.string().optional(),
  "skos:inScheme": z.object({ "@id": z.string(), "skos:prefLabel": z.string().optional() }).optional(),
});
export type DcConcept = z.infer<typeof DcConcept>;

/**
 * A feature of a product (`dcls:ProductFeature`, Informationspaket 1.3, ADR-054): the tools it
 * is made of, what it does and what its maker promises with it share the one template. Name
 * and description are the editors', the description HTML; the image is the icon.
 */
export const DcProductFeature = z.object({
  "@id": z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
  image: z.array(DcImage).optional(),
});
export type DcProductFeature = z.infer<typeof DcProductFeature>;

/** A display price (F-74): the amount and what it is per, e.g. 129 per "Monat". */
export const DcPriceSpecification = z.object({
  "@id": z.string(),
  price: z.number().optional(),
  unitText: z.string().optional(),
});
export type DcPriceSpecification = z.infer<typeof DcPriceSpecification>;

/** An offer of a product (`dcls:ProductOffer`) with its display prices; the platform's price is binding (ADR-063). */
export const DcOffer = z.object({
  "@id": z.string(),
  priceSpecification: z.array(DcPriceSpecification).optional(),
});
export type DcOffer = z.infer<typeof DcOffer>;

/** A question of the product's FAQ (`dc:faq`, Informationspaket 1.3); question and answer are HTML from the editors. */
export const DcQuestion = z.object({
  "@id": z.string(),
  name: z.string().optional(),
  acceptedAnswer: z.array(z.object({ "@id": z.string(), text: z.string().optional() })).optional(),
});
export type DcQuestion = z.infer<typeof DcQuestion>;

/**
 * One product as the endpoint describes it (schema.org `Product`, Informationspaket 1.3). The
 * fields after the concepts came with the model of 2026-10-14 and feed the product page of the
 * website (07 §6, AP-F3.1); the portal receives only what `DC_PRODUCT_FIELDS` names.
 */
export const DcProduct = z.object({
  /** the dataCycle UUID; `Product.datacycleId` on the platform side (ADR-054) */
  "@id": Uuid,
  /** display name */
  name: z.string().min(1),
  "dc:slug": z.string().min(1),
  /** short description for tiles, HTML; editors may leave it empty, then dataCycle omits it */
  disambiguatingDescription: z.string().optional(),
  /** description for the detail page, HTML; may be missing like the short one */
  description: z.string().optional(),
  /** key visual first (Informationspaket 1.3) */
  image: z.array(DcImage).optional(),
  /** the maker; dataCycle sends a single link as a list of one */
  brand: z.array(DcBrand).optional(),
  /** the concepts of every tree the product is in; the category tree is one of them */
  "dc:classification": z.array(DcConcept).optional(),
  /** the product's own logo or icon, a wordmark for the AI Cockpit */
  logo: z.array(DcImage).optional(),
  /** display prices; none means "Preis auf Anfrage" */
  offers: z.array(DcOffer).optional(),
  /** the parts of the product, e.g. OpenWebUI, n8n and dataCycle MCP (ADR-033) */
  "dc:tool": z.array(DcProductFeature).optional(),
  /** what it does, one block of the product page each */
  "dc:productFeature": z.array(DcProductFeature).optional(),
  /** what its maker promises with it, e.g. set-up and support */
  "dc:commitment": z.array(DcProductFeature).optional(),
  "dc:faq": z.array(DcQuestion).optional(),
  /** the data sheet of an agent: what it reads, what it writes, how often it runs */
  "dc:reads": z.string().optional(),
  "dc:writes": z.string().optional(),
  "dc:runFrequency": z.string().optional(),
});
export type DcProduct = z.infer<typeof DcProduct>;

/** The answer of the endpoint as far as the portal reads it: JSON-LD with the items in `@graph`. */
export const DcProductListResponse = z.object({
  "@graph": z.array(z.unknown()),
});

/**
 * The products of an endpoint answer. Never throws: an answer it does not understand is an
 * empty list, an entry it does not understand is left out, so one broken entry in dataCycle
 * costs that product its texts and nothing else. The caller falls back to the platform's name.
 */
export function parseDcProducts(value: unknown): DcProduct[] {
  const answer = DcProductListResponse.safeParse(value);
  if (!answer.success) return [];
  return answer.data["@graph"].flatMap((item) => {
    const product = DcProduct.safeParse(item);
    return product.success ? [product.data] : [];
  });
}

/**
 * The concepts of one tree, by the tree's UUID (Informationspaket 1.4: filters and facets work
 * with UUIDs, labels are content), in dataCycle's order, each concept once.
 */
export function conceptsOf(product: Pick<DcProduct, "dc:classification">, schemeId: string): DcConcept[] {
  const seen = new Set<string>();
  return (product["dc:classification"] ?? []).filter((concept) => {
    if (concept["skos:inScheme"]?.["@id"] !== schemeId || seen.has(concept["@id"])) return false;
    seen.add(concept["@id"]);
    return true;
  });
}

/** The slug of the product's brand as dataCycle names it, or undefined without a brand or slug. */
export function brandSlugOf(product: Pick<DcProduct, "brand">): string | undefined {
  return product.brand?.[0]?.["dc:slug"];
}
